"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "react-use-cart";

interface PaymentMethod {
  _id: string; // string instead of ObjectId
  displayName: string;
  type: string;
  name: string;
  enabled: boolean;
}

interface OrderFormProps {
  paymentMethods: PaymentMethod[];
  isGuest: boolean;
  email: string;
  balance: number
}

export default function CheckoutPaymentsForm({ paymentMethods, isGuest, email, balance }: OrderFormProps) {
  const router = useRouter();
  const { removeItem, isEmpty, items, updateItemQuantity, emptyCart } = useCart()

  const subtotal = items.reduce((acc, item) => acc + item.price * (item.quantity || 0), 0);

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodType, setPaymentMethodType] = useState("");
  const [emailInclude, setEmail] = useState(email);

  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{ name: string; percentage: number } | null>(null);
  const [rates, setRates] = useState<[{coin: string; minimum: number; price: number; minimumInUsd: number}]|null>(null);
  const [ratesLoading, setRatesLoading] = useState(false);

  const discountAmount = appliedPromo ? Math.round(subtotal * (appliedPromo.percentage / 100) * 100) / 100 : 0;
  const total = Math.round((subtotal - discountAmount) * 100) / 100;

  useMemo(() => {
    updateRates()
  }, [rates]);

  async function updateRates() {
    if(rates) return;

    setRatesLoading(true)
    try {
      const res = await fetch("/api/rates", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();

      if (res.ok) {
        setRates(data.data);
      } else {
        setRates(null);
      }
    } catch {
          
    }
    setRatesLoading(false)
  }

  async function applyPromoCode() {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMessage("");
    try {
      const res = await fetch("/api/checkout/promocode", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoCode }),
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        setAppliedPromo({ name: data.name, percentage: data.percentage });
        setPromoMessage(`Promo code applied: ${data.percentage}% off`);
      } else {
        setAppliedPromo(null);
        setPromoMessage(data.error || "Invalid promo code");
      }
    } catch {
      setPromoMessage("Failed to validate promo code");
    }
    setPromoLoading(false);
  }

  function removePromoCode() {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoMessage("");
  }

  function getRateFor(coin: string) {
    if(!rates) return 2;    
    let coinIndex = rates.map(e => e.coin).indexOf(coin);

    if(coinIndex == -1) return 5;

    return rates[coinIndex].minimumInUsd
  }

  async function checkout() {
    setLoading(true);

    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethod: paymentMethod, paymentMethodType: paymentMethodType, topup: false, cartItems: items, guest: isGuest, email: emailInclude, promoCode: appliedPromo?.name || undefined }),
      });

      if (res.ok) {
        let resp = await res.json()

        router.push("/order/" + resp["orderId"])

        setPaymentMethod("");
        setPaymentMethodType("");
        emptyCart();
      } else {
        const data = await res.json();
        console.log(data.error ?? "ERROR ON fetching /api/checkout");
      }
    } catch (e) {
      console.log(e)
    }

    setLoading(false);
  }

  return (
    <>
      <div className="bg-[rgba(249,115,22,0.1)]  border border-orange-600/25 rounded-2xl shadow-2xl w-full lg:w-[65%]">
        <div className="border-b border-orange-600/25 rounded-2xl bg-gradient-to-r from-orange-600/10 to-transparent">
          <h2 className="text-2xl font-bold text-center py-4 px-5 text-white">Shopping Cart</h2>
        </div>
        <div className="p-6">
          {isEmpty ?
            <>
              <div className="flex flex-col items-center justify-center py-12">
                <svg className="w-24 h-24 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <h4 className="text-xl font-semibold mb-2 text-white">Your cart is empty</h4>
                <p className="text-gray-400 mb-6">Add some items to get started</p>
                <button
                  onClick={() => window.location.href = "/products"}
                  className="px-8 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all"
                >
                  Browse Products
                </button>
              </div>
            </>
            : <>
              {items.map((item) => (
                <div key={item.id} className="flex flex-col sm:flex-row items-center bg-[rgba(249,115,22,0.1)] border border-gray-800 rounded-xl p-4 gap-4 mb-3 hover:border-orange-600/50 transition-all">
                  <Image
                    src={item.image}
                    alt={item.name}
                    width={100}
                    height={100}
                    className="rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                    <h3 className="text-base font-semibold text-white mb-1">
                      {item.name}
                    </h3>
                    <div className="text-orange-400 font-semibold">
                      ${item.price.toFixed(2)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-[rgba(249,115,22,0.1)] border border-gray-700 rounded-lg px-3 py-2">
                    <button 
                      className="w-8 h-8 flex items-center justify-center text-white hover:bg-orange-600 rounded transition-all disabled:opacity-50"
                      onClick={() => {
                        let quantity = item.quantity || 1;
                        if (quantity <= item.minBuy) {
                          quantity = item.minBuy;
                        } else if (quantity > item.stock) {
                          quantity = item.stock;
                        } else {
                          quantity--;
                        }
                        updateItemQuantity(item.id, quantity)
                      }}
                      disabled={(item.quantity || 0) <= 1}
                      aria-label="Decrease quantity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input 
                      type="number" 
                      min={item.minBuy} 
                      max={item.stock} 
                      value={item.quantity} 
                      onChange={(e) => updateItemQuantity(item.id, +e.target.value)}
                      className="w-12 bg-transparent text-center text-white focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                    <button
                      className="w-8 h-8 flex items-center justify-center text-white hover:bg-orange-600 rounded transition-all"
                      onClick={() => {
                        let quantity = item.quantity || 1;
                        if (quantity < item.minBuy) {
                          quantity = item.minBuy;
                        } else if (quantity >= item.stock) {
                          quantity = item.stock;
                        } else {
                          quantity++;
                        }
                        updateItemQuantity(item.id, quantity)
                      }}
                      aria-label="Increase quantity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  <button 
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                    onClick={() => removeItem(item.id)}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

              ))}
            </>
          }

        </div>
        <div className="border-t border-orange-600/25 bg-gradient-to-b from-transparent to-orange-600/5 p-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center text-gray-400">
              <span className="text-sm">Subtotal</span>
              <span className="text-sm font-semibold">${subtotal.toFixed(2)}</span>
            </div>
            {appliedPromo && (
              <div className="flex justify-between items-center text-green-400">
                <span className="text-sm">Discount ({appliedPromo.percentage}%)</span>
                <span className="text-sm font-semibold">-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-gray-800 pt-3 flex justify-between items-center">
              <span className="text-xl font-bold text-white">Total</span>
              <span className="text-2xl font-bold text-orange-400">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[rgba(249,115,22,0.1)] border border-orange-600/25 w-full rounded-2xl">
        <div className="border-b border-orange-600/25 rounded-2xl bg-gradient-to-r from-orange-600/10 to-transparent p-6">
          <h2 className="text-2xl font-bold mb-1 text-white">Payment Method</h2>
          <p className="text-sm text-gray-400">Choose your preferred payment option</p>
        </div>

        {!isGuest ?
          <div className="p-6">
            <button
              onClick={() => {
                setPaymentMethod("balance")
                setPaymentMethodType("balance")
              }}
              className={`w-full p-5 rounded-xl border-2 transition-all ${
                paymentMethod === "balance"
                  ? 'bg-gradient-to-br from-orange-600/30 to-orange-800/30 border-orange-500'
                  : 'bg-[rgba(249,115,22,0.1)] border-gray-800 hover:border-orange-600/50'
              }`}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-white">Account Balance</h3>
                    {paymentMethod === "balance" && (
                      <span className="ml-2 px-2 py-1 text-xs bg-orange-500 text-white rounded-full">Selected</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">Use your pre-loaded balance</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Available</p>
                    <p className="text-2xl font-bold text-orange-400">${balance.toFixed(2)}</p>
                  </div>
                  <Link 
                    href="/account/balance" 
                    className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 transition-all"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Top Up
                  </Link>
                </div>
              </div>
            </button>
          </div>
          :
          <div className="p-6">
            <div className="bg-[rgba(249,115,22,0.1)] border-2 border-gray-800 rounded-xl p-5">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1 text-white">Guest Checkout</h3>
                  <p className="text-sm text-gray-400 mb-4">Enter your email to continue</p>
                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 bg-[rgba(249,115,22,0.1)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        <div className="px-4 sm:px-10 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {!ratesLoading ? 
            <>
            {paymentMethods.map((payment) => (
              <button
                key={payment._id.toString()}
                onClick={() => {
                    setPaymentMethod(payment.name)
                    setPaymentMethodType(payment.type)
                  }}
                className={`relative group rounded-xl p-4 transition-all duration-300 ${
                  paymentMethod === payment.name
                    ? 'bg-gradient-to-br from-orange-600/30 to-orange-800/30 border-2 border-orange-500 shadow-lg shadow-orange-500/20'
                    : `bg-[rgb(46,26,26)] border-2 border-gray-800 ${((getRateFor(payment.name)) > total) ? `disabled:bg-gray-800 disabled:opacity-50` : `hover:border-orange-600/50 hover:bg-[rgb(64,37,37)]`}`
                }`}
                disabled={(getRateFor(payment.name)) > total}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden transition-transform duration-300 ${
                    paymentMethod === payment.name ? 'scale-110' : 'group-hover:scale-105'
                  }`}>
                    <Image
                      alt={payment.name}
                      width={48}
                      height={48}
                      className="object-contain"
                      src={`/assets/${payment.name}.png`}
                    />
                  </div>
                  <span className="text-sm font-semibold text-white text-center">
                    {payment.displayName}
                  </span>
                </div>
                {paymentMethod === payment.name && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
            </>
            : 
            <>
              <div className="flex items-center gap-2 px-1 py-2 text-sm text-gray-400">
                <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce [animation-delay:300ms]" />
                <span className="ml-1">Loading crypto currency rates</span>
              </div>
            </>
          }
          </div>
        </div>

        <div className="p-6 border-t border-orange-600/25">
          <div className="mb-4">
            {appliedPromo ? (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-green-400 font-semibold flex-1">{appliedPromo.name} - {appliedPromo.percentage}% off</span>
                <button onClick={removePromoCode} className="text-gray-400 hover:text-red-400 transition-colors text-sm">
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromoCode(); } }}
                  className="flex-1 px-4 py-3 bg-[rgba(249,115,22,0.1)] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
                />
                <button
                  onClick={applyPromoCode}
                  disabled={promoLoading || !promoCode.trim()}
                  className="px-5 py-3 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {promoLoading ? "..." : "Apply"}
                </button>
              </div>
            )}
            {promoMessage && !appliedPromo && (
              <p className="text-sm text-red-400 mt-2">{promoMessage}</p>
            )}
          </div>
          <button
            onClick={async () => await checkout()}
            disabled={paymentMethod === "" || isEmpty || (balance < total && paymentMethod == "balance") || (emailInclude === "" || (!emailInclude.includes("@") || !emailInclude.includes(".")))}
            className="w-full py-4 bg-gradient-to-r from-orange-600 to-orange-700 text-white font-bold text-lg rounded-xl hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-orange-500/20 disabled:shadow-none flex items-center justify-center gap-3"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {!isGuest ? "Complete Purchase" : "Checkout as Guest"}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
